import { clamp } from './utils.js';

/**
 * @typedef {object} UiConfig
 * @property {number} BPM_MIN
 * @property {number} BPM_MAX
 */

/** @param {any} el @param {UiConfig} config */
export function createUi(el, { BPM_MIN, BPM_MAX }) {
    /** @param {string} msg */
    function logLine(msg) {
        const now = new Date();
        const stamp = now.toLocaleTimeString([], {
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit',
        });
        const line = `[${stamp}] ${msg}\n`;
        if (el.log) {
            el.log.textContent = (line + el.log.textContent).slice(0, 900);
        }
    }

    /** @param {number} bpm */
    function updateBpmButtonState(bpm) {
        if (!el.bpmDown || !el.bpmUp) return;
        el.bpmDown.disabled = bpm <= BPM_MIN;
        el.bpmUp.disabled = bpm >= BPM_MAX;
    }

    /** @param {number} bpm */
    function updateTempoUI(bpm) {
        if (el.bpmNumber) el.bpmNumber.value = String(bpm);
        updateBpmButtonState(bpm);
    }

    /** @param {number} beatsPerBar */
    function buildBeatLights(beatsPerBar) {
        if (!el.lights) return;
        el.lights.innerHTML = '';
        for (let i = 0; i < beatsPerBar; i++) {
            const d = document.createElement('div');
            d.className = 'beat-light';
            d.dataset['idx'] = String(i);
            el.lights.appendChild(d);
        }
    }

    /** @param {number} beat @param {{ role?: "bar" | "beat" | "sub" }} [opts] */
    function renderBeatLights(beat, { role } = {}) {
        if (!el.lights) return;
        const dots = el.lights.querySelectorAll('.beat-light');
        dots.forEach(
            /** @param {Element} dot @param {number} idx */ (dot, idx) => {
                const beatNum = idx + 1;
                dot.classList.remove('active', 'accent');
                if (beatNum === beat) {
                    dot.classList.add('active');
                    if (role === 'bar') dot.classList.add('accent');
                }
            },
        );
    }

    /** @param {string} rhythmString @param {number} steps */
    function renderRhythmString(rhythmString, steps) {
        const chars = rhythmString ? rhythmString.split(' ') : [];

        /** @param {HTMLElement | null} target */
        const renderInto = (target) => {
            if (!target) return;
            target.innerHTML = '';
            const wrapper = document.createElement('span');
            wrapper.className = 'rhythmSteps';
            const allSteps = chars.map((ch, idx) => {
                const span = document.createElement('span');
                span.className = 'rhythmStep';
                span.dataset['step'] = String(idx);
                span.textContent = ch || '.';
                return span;
            });

            const singleLine = document.createElement('span');
            singleLine.className = 'rhythmLine';
            for (const step of allSteps) singleLine.appendChild(step.cloneNode(true));
            wrapper.appendChild(singleLine);
            target.appendChild(wrapper);

            const fitsOneLine =
                target.clientWidth > 0 && target.scrollWidth <= target.clientWidth + 1;
            if (fitsOneLine || allSteps.length <= 1) return;

            wrapper.innerHTML = '';
            const topCount = Math.ceil(allSteps.length / 2);
            const topLine = document.createElement('span');
            topLine.className = 'rhythmLine';
            const bottomLine = document.createElement('span');
            bottomLine.className = 'rhythmLine';
            allSteps.forEach((step, idx) => {
                if (idx < topCount) topLine.appendChild(step.cloneNode(true));
                else bottomLine.appendChild(step.cloneNode(true));
            });
            wrapper.appendChild(topLine);
            if (bottomLine.childElementCount > 0) wrapper.appendChild(bottomLine);
        };

        const tightenStart = 14;
        const tightenEnd = 28;
        const t = clamp((steps - tightenStart) / (tightenEnd - tightenStart), 0, 1);
        /** @param {number} a @param {number} b @param {number} v */
        const lerp = (a, b, v) => a + (b - a) * v;
        const tEase = t * 0.8;
        const gap = lerp(12, 6, tEase);
        const letter = lerp(1, 0.4, tEase);
        const widthCh = lerp(1.35, 1.05, tEase);
        const padX = lerp(6, 3, tEase);
        const padY = lerp(4, 2, tEase);

        el.rhythmStringDisplay.style.setProperty('--rhythm-gap', `${gap}px`);
        el.rhythmStringDisplay.style.setProperty('--rhythm-letter', `${letter}px`);
        el.rhythmStringDisplay.style.setProperty('--rhythm-step-width', `${widthCh}ch`);
        el.rhythmStringDisplay.style.setProperty('--rhythm-step-pad-x', `${padX}px`);
        el.rhythmStringDisplay.style.setProperty('--rhythm-step-pad-y', `${padY}px`);

        renderInto(el.rhythmStringDisplay);
    }

    function clearRhythmHighlight() {
        /** @param {HTMLElement | null} target */
        const clearIn = (target) => {
            if (!target) return;
            target
                .querySelectorAll('.rhythmStep.isActive')
                .forEach((node) => node.classList.remove('isActive'));
        };
        clearIn(el.rhythmStringDisplay);
    }

    /** @param {number} stepInBar */
    function highlightRhythmStep(stepInBar) {
        /** @param {HTMLElement | null} target */
        const markIn = (target) => {
            if (!target) return;
            target
                .querySelectorAll('.rhythmStep.isActive')
                .forEach((node) => node.classList.remove('isActive'));
            const node = target.querySelector(`.rhythmStep[data-step="${stepInBar}"]`);
            if (node) node.classList.add('isActive');
        };
        markIn(el.rhythmStringDisplay);
    }

    return {
        logLine,
        updateTempoUI,
        updateBpmButtonState,
        buildBeatLights,
        renderBeatLights,
        renderRhythmString,
        clearRhythmHighlight,
        highlightRhythmStep,
    };
}
